import React from 'react';
import { useIntl } from 'react-intl';
import { ProposalBriefDraft } from '../utils';
import { AITextField } from './AITextField';
import { AITextareaField } from './AITextareaField';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';

interface Props {
  briefDraft: ProposalBriefDraft;
  onBriefDraftChange: <K extends keyof ProposalBriefDraft>(
    key: K,
    value: ProposalBriefDraft[K],
  ) => void;
  onGenerateBlocks: React.MouseEventHandler<HTMLButtonElement>;
  isGeneratingBlocks: boolean;
}

export const ProposalAIGeneratorBriefSection: React.FC<Props> = ({
  briefDraft,
  onBriefDraftChange,
  onGenerateBlocks,
  isGeneratingBlocks,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="proposal-ai-generator__section">
      <div className="proposal-ai-generator__section-heading">
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.brief.description' })}
        </p>
      </div>

      <div className="proposal-ai-generator__field-list">
        <AITextField
          id="proposal-ai-brief-title"
          label={formatMessage({ id: 'proposal-ai.brief.proposal-name' })}
          value={briefDraft.proposalNameSuggestion}
          onChange={(value) =>
            onBriefDraftChange('proposalNameSuggestion', value)
          }
          dataCy="proposal-ai-brief-title"
        />

        <AITextareaField
          id="proposal-ai-brief-summary"
          label={formatMessage({ id: 'proposal-ai.brief.project-summary' })}
          value={briefDraft.projectSummary}
          onChange={(value) => onBriefDraftChange('projectSummary', value)}
        />

        <AITextareaField
          id="proposal-ai-brief-deliverables"
          label={formatMessage({ id: 'proposal-ai.brief.deliverables' })}
          value={briefDraft.deliverables}
          onChange={(value) => onBriefDraftChange('deliverables', value)}
          helpText={formatMessage({ id: 'proposal-ai.brief.list-help' })}
        />

        <AITextareaField
          id="proposal-ai-brief-sections"
          label={formatMessage({
            id: 'proposal-ai.brief.recommended-sections',
          })}
          value={briefDraft.recommendedSections}
          onChange={(value) => onBriefDraftChange('recommendedSections', value)}
          helpText={formatMessage({ id: 'proposal-ai.brief.list-help' })}
        />

        <AITextareaField
          id="proposal-ai-brief-assumptions"
          label={formatMessage({ id: 'proposal-ai.brief.assumptions' })}
          value={briefDraft.assumptions}
          onChange={(value) => onBriefDraftChange('assumptions', value)}
          helpText={formatMessage({ id: 'proposal-ai.brief.list-help' })}
        />

        <AITextareaField
          id="proposal-ai-brief-exclusions"
          label={formatMessage({ id: 'proposal-ai.brief.exclusions' })}
          value={briefDraft.exclusions}
          onChange={(value) => onBriefDraftChange('exclusions', value)}
          helpText={formatMessage({ id: 'proposal-ai.brief.list-help' })}
        />
      </div>

      <div className="proposal-ai-generator__action-row">
        <ProposalAIPrimaryButton
          label={
            isGeneratingBlocks
              ? formatMessage({ id: 'proposal-ai.generate.loading' })
              : formatMessage({ id: 'proposal-ai.generate.action' })
          }
          onClick={onGenerateBlocks}
          disabled={isGeneratingBlocks}
          btnSelector="proposal-ai-generate-blocks"
        />
      </div>
    </div>
  );
};
